import React, { Fragment, FC, memo, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import LazyLoader from 'react-lazyload';

import { Showstopper } from '@browsing/get-inspired';
import { TopPicks4U } from '@browsing/top-picks-4U';
import { Campaign } from '@shared/types/mylounge';

import {
    getClientDeviceWidth,
    getClientDeviceHeight,
    getIsMobileScreen,
} from '@client/redux/modules/clientDevice/selectors';
import { getCampaignMobileImage } from '@client/helpers/images';
import {
    SECOND_SHOWSTOPPER_FIXED_POSITION,
    SHOWSTOPPER_FIXED_POSITION,
} from '@client/config/constants';
import { useVariant } from '@client/lib/assignments';

import { getGender } from '@client/redux/modules/customer';
import { CustomerGender } from '@client/components/mylounge/campaigns/grid/constants';
import { CategoryTabIds } from '@client/components/mylounge/banner/constants';
import { CampaignCard } from '../components/campaign-card';
import * as S from './mobile-campaigns.styles';
import { ShowstopperContent } from '../components/showstopper';
import { ShowstopperMobileContainer } from '../components/showstopper.styles';

interface Props {
    campaigns: Campaign[];
    showstopper?: ShowstopperContent;
    isPlusEnabled: boolean;
    categoryTabId: CategoryTabIds;
    injectInterstitialComponent?: (campaign: Campaign, index: number) => ReactNode;
}

const LABEL_HEIGHT = 60;

export const MobileCampaigns: FC<Props> = ({
    campaigns,
    showstopper,
    categoryTabId,
    isPlusEnabled,
    injectInterstitialComponent,
}) => {
    const width = useSelector(getClientDeviceWidth);
    const height = useSelector(getClientDeviceHeight);
    const isMobileScreen = useSelector(getIsMobileScreen);
    const gender = useSelector(getGender)?.toUpperCase() as CustomerGender;

    if (!campaigns.length) {
        // TODO: return valid translated text
        return null;
    }

    const imgRatio = 0.58;
    const normalHeight = width * imgRatio;

    return (
        <S.Container id="campaigns-open">
            {campaigns.map((campaign, index) => {
                const imageUrl = getCampaignMobileImage(campaign);
                return (
                    <Fragment key={campaign.campaignId}>
                        {index === SHOWSTOPPER_FIXED_POSITION &&
                            categoryTabId === CategoryTabIds.ALL && (
                                <ShowstopperMobileContainer>
                                    <TopPicks4U isMobileScreen gender={gender} />
                                </ShowstopperMobileContainer>
                            )}

                        {showstopper && index === SECOND_SHOWSTOPPER_FIXED_POSITION && (
                            <ShowstopperMobileContainer>
                                <Showstopper
                                    title={showstopper.title}
                                    content={showstopper.content}
                                    media={showstopper.media}
                                    carouselItems={showstopper.campaigns}
                                    position={showstopper.position}
                                    categoryTabId={showstopper.categoryTabId}
                                    isMobileScreen={isMobileScreen}
                                    visitorID={showstopper.visitorID}
                                />
                            </ShowstopperMobileContainer>
                        )}

                        {injectInterstitialComponent &&
                            injectInterstitialComponent(campaign, index)}

                        <LazyLoader offset={height} height={normalHeight} key={campaign.campaignId}>
                            <S.CampaignContainer>
                                <CampaignCard
                                    key={campaign.campaignId}
                                    campaign={campaign}
                                    isPlusEnabled={isPlusEnabled}
                                    position={`${index}|1`}
                                    imageUrl={imageUrl}
                                    minHeight={normalHeight + LABEL_HEIGHT}
                                />
                            </S.CampaignContainer>
                        </LazyLoader>
                    </Fragment>
                );
            })}
        </S.Container>
    );
};

export default memo(MobileCampaigns);
